<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-1">
        <i class="far fa-shopping-cart"></i> Invoiced Glass Summary Detailed Report
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-1">
        <div class="row">
          <div class="col-12">

            <ActionDropMenu class="float-right">
              <DropdownMenuItem @click="exportDataCSV" icon="fa-file-csv">Export CSV</DropdownMenuItem>
            </ActionDropMenu>

            <ReportFilterPopover
              class="float-right mr-2"
              headerTitle="Report Filter"
              :showApplyButton="true"
              :showResetButton="true"
              @apply="applySummaryFilter"
              @reset="resetSummaryFilter">
              <template v-slot:popover>
                <ReportFilterGroup label="Date Range">
                  <ReportDateRangeSelect 
                      :startDate="editingSummaryFilter.startDate"
                      :endDate="editingSummaryFilter.endDate"
                      @updateStartDate="editingSummaryFilter.startDate = $event"
                      @updateEndDate="editingSummaryFilter.endDate = $event"
                    />
                </ReportFilterGroup>

                <ReportFilterGroup label="Contact">
                  <ReportContactSelect
                    v-model="editingSummaryFilter.contactId"
                    @updateName="editingSummaryFilter.contactName = $event"
                  />
                </ReportFilterGroup>

                <ReportFilterGroup label="Stock Filter">
                  <FormMultiSelect v-model="editingSummaryFilter.stockList" :options="stockLookupList" />
                </ReportFilterGroup>

                <ReportFilterGroup label="Price Group Filter">
                  <FormMultiSelect v-model="editingSummaryFilter.priceGroupList" :options="priceGroupLookup" />
                </ReportFilterGroup>
                
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedSummaryFilter.startDate | formatDate}} - {{appliedSummaryFilter.endDate | formatDate}}
                </div>
                <div v-if="appliedSummaryFilter.priceGroupList">
                  <i class="far fa-money-check-edit-alt mr-2"></i>{{appliedSummaryFilter.priceGroupList.length}} Price Group{{displayCountPlural(appliedSummaryFilter.priceGroupList.length)}}
                </div>
                <div v-if="appliedSummaryFilter.stockList && appliedSummaryFilter.stockList.length > 0">
                  <i class="far fa-inventory mr-2"></i>{{appliedSummaryFilter.stockList.length}} Stock{{displayCountPlural(appliedSummaryFilter.stockList.length)}}
                </div>
                <div v-if="appliedSummaryFilter.contactId">
                  <i class="far fa-user-circle mr-2"></i>{{appliedSummaryFilter.contactName}}
                </div>
              </template>
            </ReportFilterPopover>

            <ContentTitle>Invoice Summary - Glass Detailed Report</ContentTitle>
            <ContentSubTitle>Displays detailed sales data for stock items invoiced during a date range</ContentSubTitle>
            <ContentSubTitle>Based on invoices created during the date range. All prices ex-GST</ContentSubTitle>
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-12">

            <LoadingPanel :loading="loading" :loadingError="loadingError" />
            <div v-if="!loading">
              
              <StaticDataTable class="static-table-list" :striped="false">
                <template v-slot:header>
                  <tr>
                    <th> </th>
                    <th> </th>
                    <th :colspan="displayPriceGroupList.length" class="text-center table-data-alternate-column">Price Group Breakdown</th>
                    <th></th>
                    <th></th>
                  </tr>
                  <tr>
                    <th>Stock</th>
                    <th>Total</th>
                    <th class="table-data-alternate-column" v-for="priceGroup of displayPriceGroupList" :key="priceGroup.id">{{priceGroup.name}}</th>
                    <th>Services</th>
                  </tr>
                </template>
                <template v-slot:body>
                  <template v-for="stock of reportData.stocks">
                    <tr class="static-datatable-striped" :key="`main-${stock.id}`">
                      <td class="font-weight-bold"><LinkStock :id="stock.id" :name="stock.name" /></td>
                      <td class="font-weight-bold">{{formatCurrency(stock.totalSales)}}</td>
                      <td class="font-weight-bold" v-for="priceGroupData of stock.priceGroupList" :key="priceGroupData.id">{{formatCurrency(priceGroupData.totalSales)}}</td>
                      <td class="font-weight-bold">{{formatCurrency(stock.totalServicesSales)}}</td>
                    </tr>
                    <tr class="table-row-sm" :key="`sqm-${stock.id}`">
                      <td><i class="fas fa-caret-right mr-1 ml-3"></i>Square Metres</td>
                      <td>{{formatDecimal(stock.totalUnits, 5)}}m&sup2;</td>
                      <td v-for="priceGroupData of stock.priceGroupList" :key="priceGroupData.id">{{formatDecimal(priceGroupData.totalUnits, 5)}}m&sup2;</td>
                      <td> </td>
                    </tr>
                    <tr class="table-row-sm" :key="`avg-${stock.id}`">
                      <td><i class="fas fa-caret-right mr-1 ml-3"></i>Avg Sale Price / m&sup2;</td>
                      <td>{{formatCurrency(stock.avgSalePrice)}}</td>
                      <td v-for="priceGroupData of stock.priceGroupList" :key="priceGroupData.id">{{formatCurrency(priceGroupData.avgSalePrice)}}</td>
                      <td> </td>
                    </tr>
                    <tr class="table-row-sm" :key="`cogs-${stock.id}`">
                      <td><i class="fas fa-caret-right mr-1 ml-3"></i>Avg Cost of Goods Sold</td>
                      <td>
                        <Tooltip linkStyle :label="`Moving avg unit price: ${formatCurrency(stock.totalGlassUnitPriceAvg)}`">{{formatCurrency(stock.avgCOGS)}}</Tooltip>
                        &nbsp;<span class="small">(<span :class="{'financial-profit': stock.COGSProfitLoss >= 0, 'financial-loss': stock.COGSProfitLoss < 0, 'font-weight-bold': true}">+{{formatCurrency(stock.COGSProfitLoss)}}</span>)</span>
                      </td>
                      <td v-for="priceGroupData of stock.priceGroupList" :key="priceGroupData.id">
                        <Tooltip linkStyle :label="`Moving avg unit price: ${formatCurrency(stock.totalGlassUnitPriceAvg)}`">{{formatCurrency(priceGroupData.avgCOGS)}}</Tooltip>
                        &nbsp;<span class="small">(<span :class="{'financial-profit': priceGroupData.COGSProfitLoss >= 0, 'financial-loss': priceGroupData.COGSProfitLoss < 0, 'font-weight-bold': true}">+{{formatCurrency(priceGroupData.COGSProfitLoss)}}</span>)</span>
                      </td>
                      <td> </td>
                    </tr>
                    <tr class="table-row-sm" :key="`cog-percent-${stock.id}`">
                      <td><i class="fas fa-caret-right mr-1 ml-3"></i>Cost of Goods Sold Percent</td>
                      <td>{{formatPercent(stock.COGPercent)}}</td>
                      <td v-for="priceGroupData of stock.priceGroupList" :key="priceGroupData.id">{{formatPercent(priceGroupData.COGPercent)}}</td>
                      <td> </td>
                    </tr>
                    <tr class="table-row-sm" :key="`spacer-${stock.id}`">
                      <td colspan="9"> </td>
                    </tr>
                  </template>
                  <tr class="border-top table-row-sm" v-if="reportData.stocks.length > 0">
                    <td class="font-weight-bold">Total Sales</td>
                    <td class="font-weight-bold">{{formatCurrency(reportData.totals.totalSales)}}</td>
                    <td v-for="priceGroupData of reportData.totals.priceGroupList" :key="priceGroupData.id" class="font-weight-bold">
                      {{formatCurrency(priceGroupData.totalSales)}}
                    </td>
                    <td class="font-weight-bold">{{formatCurrency(reportData.totals.totalServices)}}</td>
                  </tr>
                  <tr class="table-row-sm" v-if="reportData.stocks.length > 0">
                    <td class="font-weight-bold">Total Tax</td>
                    <td class="font-weight-bold">{{formatCurrency(reportData.totals.totalTax)}}</td>
                    <td v-for="priceGroupData of reportData.totals.priceGroupList" :key="priceGroupData.id" class="font-weight-bold">
                      {{formatCurrency(priceGroupData.totalTax)}}
                    </td>
                    <td class="font-weight-bold">{{formatCurrency(reportData.totals.totalServicesTax)}}</td>
                  </tr>
                  <tr class="table-row-sm" v-if="reportData.stocks.length > 0">
                    <td class="font-weight-bold">Gross Total</td>
                    <td class="font-weight-bold">{{formatCurrency(reportData.totals.totalSales + reportData.totals.totalTax)}}</td>
                    <td v-for="priceGroupData of reportData.totals.priceGroupList" :key="priceGroupData.id" class="font-weight-bold">
                      {{formatCurrency(priceGroupData.totalSales + priceGroupData.totalTax)}}
                    </td>
                    <td class="font-weight-bold">{{formatCurrency(reportData.totals.totalServices + reportData.totals.totalServicesTax)}}</td>
                  </tr>
                  <tr class="table-row-sm" v-if="reportData.stocks.length > 0">
                    <td class="font-weight-bold">Total COGS</td>
                    <td class="font-weight-bold">
                      {{formatCurrency(reportData.totals.totalCOGS)}}
                      &nbsp;<span class="small">(<span :class="{'financial-profit': reportData.totals.totalCOGSProfitLoss >= 0, 'financial-loss': reportData.totals.totalCOGSProfitLoss < 0, 'font-weight-bold': true}">+{{formatCurrency(reportData.totals.totalCOGSProfitLoss)}}</span>)</span>
                    </td>
                    <td v-for="priceGroupData of reportData.totals.priceGroupList" :key="priceGroupData.id" class="font-weight-bold">
                      {{formatCurrency(priceGroupData.totalCOGS)}}
                      &nbsp;<span class="small">(<span :class="{'financial-profit': priceGroupData.totalCOGSProfitLoss >= 0, 'financial-loss': priceGroupData.totalCOGSProfitLoss < 0, 'font-weight-bold': true}">+{{formatCurrency(priceGroupData.totalCOGSProfitLoss)}}</span>)</span>
                    </td>
                    <td> </td>
                  </tr>
                  <tr class="table-row-sm" v-if="reportData.stocks.length > 0">
                    <td class="font-weight-bold">COGS Percent</td>
                    <td class="font-weight-bold">
                      {{formatPercent(reportData.totals.COGPercent)}}
                    </td>
                    <td v-for="priceGroupData of reportData.totals.priceGroupList" :key="priceGroupData.id" class="font-weight-bold">
                      {{formatPercent(priceGroupData.COGPercent)}}
                    </td>
                    <td> </td>
                  </tr>
                  <tr class="table-row-sm" v-if="reportData.stocks.length > 0">
                     <td class="font-weight-bold">Total Square Metres</td>
                    <td class="font-weight-bold">{{formatDecimal(reportData.totals.totalUnits, 5)}}m&sup2;</td>
                    <td v-for="priceGroupData of reportData.totals.priceGroupList" :key="priceGroupData.id" class="font-weight-bold">
                      {{formatDecimal(priceGroupData.totalUnits, 5)}}m&sup2;
                    </td>
                    <td> </td>
                  </tr>
                </template>
                <template v-slot:empty v-if="reportData.stocks.length == 0">No invoice data found</template>
              </StaticDataTable>

            </div>
          </div>
        </div>

      </TabPage>
    </template>
  </TabbedBox>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'
import PriceRequestSearchableSelect from '@/modules/pricing/components/priceRequestSearchableSelect'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'
import ReportContactSelect from '@/components/reporting/reportContactSelect'

import { InventoryStockTypes } from '@/store/types'

export default {
  name: 'InvoiceGlassDetailedSummary',
  components: {
    FormInputStockCode,
    PriceRequestSearchableSelect,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup,
    ReportContactSelect
  },
  data() {
    return {
      loading: false,
      loadingError: '',

      defaultSummaryFilter: {    
        startDate: moment().local().startOf('month').toDate().toISOString(),
        endDate: moment().local().endOf('month').toDate().toISOString(),
        contactId: null,
        contactName: '',
        priceGroupList: [],
        stockList: []
      },
      editingSummaryFilter: {},
      appliedSummaryFilter: {},

      priceGroupList: [],
      priceGroupLookup: [],
      stockList: [],
      stockLookupList: [],
      reportData: {
        stockPriceGroups: [],
        stocks: [],
        totals: {}
      }
    }
  },
  computed: {
    displayPriceGroupList() {
      return this.priceGroupList.filter(priceGroup => {
        return (this.appliedSummaryFilter.priceGroupList.indexOf(priceGroup.id) != -1)
      })
    }    
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
  },
  mounted() {
    this.editingSummaryFilter = _.clone(this.defaultSummaryFilter)
    this.appliedSummaryFilter = _.clone(this.defaultSummaryFilter)

    this.loading = true
    this.requestData()
  },
  beforeDestroy() {            
  },
  methods: {
    requestData() {
      // Get config data
      Promise.all([
        this.$apiService.inventoryService.getStockList({}, { isActive: 1, type: InventoryStockTypes.glass }),
        this.$apiService.pricingService.getStockPriceGroupList()
      ])
      .then(results => {
        this.stockList = results[0].data
        this.stockLookupList = this.stockList.map(stock => {
          return {
            value: stock.id,
            name: `${stock.code} | ${stock.name}`
          }
        })

        this.priceGroupList = results[1].data.filter(priceGroup => priceGroup.isActive)
        this.priceGroupLookup = this.priceGroupList.map(priceGroup => {
          return {
            value: priceGroup.id,
            name: priceGroup.name
          }
        })
        // Default to all price groups selected
        this.defaultSummaryFilter.priceGroupList = this.priceGroupList.map(priceGroup => priceGroup.id)
        this.resetSummaryFilter()
      })
    },
    requestReportData() {
      var params = {
        startDate: this.appliedSummaryFilter.startDate,
        endDate: this.appliedSummaryFilter.endDate,
        priceGroupIdList: this.appliedSummaryFilter.priceGroupList,
        contactId: this.appliedSummaryFilter.contactId
      }
      if(this.appliedSummaryFilter.stockList && this.appliedSummaryFilter.stockList.length > 0)
        params.stockIdList = this.appliedSummaryFilter.stockList

      this.loading = true
      this.loadingError = ''
      this.$apiService.analyticsService.getInvoiceValueGlassDetailedList(params)
      .then(response => {
        this.reportData = response.data
      })    
      .catch(err => {
        console.error(err)
        this.loadingError = 'Error loading data'
      })
      .finally(() => {
        this.loading = false
      })
    },
    exportDataCSV() {
      var params = {
        startDate: this.appliedSummaryFilter.startDate,
        endDate: this.appliedSummaryFilter.endDate,
        priceGroupIdList: this.appliedSummaryFilter.priceGroupList,
        contactId: this.appliedSummaryFilter.contactId,
        format: 'csv'
      }
      if(this.appliedSummaryFilter.stockList && this.appliedSummaryFilter.stockList.length > 0)
        params.stockIdList = this.appliedSummaryFilter.stockList

      let queryString = buildUrl({ queryParams: params })

      this.$apiService.reportingService.generateReportSignedURL(`/intelligence/accounts/invoices/detailedlist/glass${queryString}`)
      .then(response => {
        window.location.href = response.data.url
      })
    },
    applySummaryFilter() {
      this.appliedSummaryFilter = _.clone(this.editingSummaryFilter)

      this.requestReportData()
    },
    resetSummaryFilter() {
      this.editingSummaryFilter = _.clone(this.defaultSummaryFilter)
      this.appliedSummaryFilter = _.clone(this.defaultSummaryFilter)

      this.requestReportData()
    }
  }
}
</script>
